import { useState } from "react";
import { Users, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import PageHeader from "@/components/PageHeader";
import SearchBox from "@/components/SearchBox";
import { maskCPF, maskPhone } from "@/lib/masks";
import EmptyState from "@/components/EmptyState";
import { useCrudList, crudCreate, crudUpdate, crudDelete } from "@/lib/crud";

const empty = {
  nome: "", cpf: "", parentesco: "", telefone: "", email: "",
  responsavel_financeiro: false, observacoes: "",
};

export default function Responsaveis() {
  const [q, setQ] = useState("");
  const { items, loading, setItems } = useCrudList("/responsaveis", { q });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setOpen(true); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (editing) {
        const upd = await crudUpdate("/responsaveis", editing.id, payload);
        setItems((prev) => prev.map((x) => x.id === upd.id ? upd : x));
        toast.success("Responsável atualizado");
      } else {
        const created = await crudCreate("/responsaveis", payload);
        setItems((prev) => [created, ...prev]);
        toast.success("Responsável cadastrado");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await crudDelete("/responsaveis", deleteId);
      setItems((prev) => prev.filter((x) => x.id !== deleteId));
      toast.success("Responsável excluído");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    finally { setDeleteId(null); }
  };

  return (
    <div data-testid="responsaveis-page">
      <PageHeader
        icon={Users}
        title="Responsáveis"
        description="Cadastro de pais, tutores e responsáveis dos pacientes."
        actions={<Button onClick={openNew} data-testid="responsavel-new-btn"><Plus className="h-4 w-4 mr-1" />Novo responsável</Button>}
      />

      <div className="mb-4"><SearchBox value={q} onChange={setQ} placeholder="Buscar por nome, CPF ou email…" testId="responsaveis-search" /></div>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-muted-foreground">Carregando…</div>
          ) : items.length === 0 ? (
            <div className="p-6"><EmptyState icon={Users} title="Nenhum responsável" description="Cadastre pais, tutores ou avós dos pacientes." action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1" />Novo responsável</Button>} /></div>
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Nome</TableHead><TableHead>Parentesco</TableHead>
                <TableHead>Telefone</TableHead><TableHead>Email</TableHead>
                <TableHead>Financeiro</TableHead><TableHead className="w-24"></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((r) => (
                  <TableRow key={r.id} data-testid={`responsavel-row-${r.id}`}>
                    <TableCell className="font-medium">{r.nome}</TableCell>
                    <TableCell className="text-muted-foreground">{r.parentesco || "—"}</TableCell>
                    <TableCell>{r.telefone || "—"}</TableCell>
                    <TableCell>{r.email || "—"}</TableCell>
                    <TableCell>{r.responsavel_financeiro ? <Badge variant="secondary">Sim</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell><div className="flex justify-end gap-1">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => setDeleteId(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </div></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader><DialogTitle>{editing ? "Editar responsável" : "Novo responsável"}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <F label="Nome" req><Input required value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="responsavel-form-nome" /></F>
              <F label="Parentesco"><Input value={form.parentesco || ""} placeholder="Mãe, pai, avó…" onChange={(e) => setForm({ ...form, parentesco: e.target.value })} /></F>
              <F label="CPF"><Input placeholder="000.000.000-00" value={form.cpf || ""} onChange={(e) => setForm({ ...form, cpf: maskCPF(e.target.value) })} /></F>
              <F label="Telefone"><Input placeholder="(00) 00000-0000" value={form.telefone || ""} onChange={(e) => setForm({ ...form, telefone: maskPhone(e.target.value) })} /></F>
              <F label="Email"><Input type="email" value={form.email || ""} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
              <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
                <div>
                  <p className="text-sm font-medium">Responsável financeiro</p>
                  <p className="text-xs text-muted-foreground">Recebe cobranças em seu nome</p>
                </div>
                <Switch checked={!!form.responsavel_financeiro} onCheckedChange={(v) => setForm({ ...form, responsavel_financeiro: v })} data-testid="responsavel-form-fin" />
              </div>
            </div>
            <F label="Observações"><Textarea rows={2} value={form.observacoes || ""} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} /></F>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving} data-testid="responsavel-form-submit" className="bg-primary hover:bg-[#3B704E]">{saving ? "Salvando…" : "Salvar"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir responsável?</AlertDialogTitle><AlertDialogDescription>Ação irreversível.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function F({ label, req, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}{req && <span className="text-destructive"> *</span>}</Label>
      {children}
    </div>
  );
}
